import type { TDocumentDefinitions, Content, TableCell } from "pdfmake/interfaces.js";
import type { DocumentBuilder, DocumentSnapshot } from "../document-engine.js";
import { documentEngine } from "../document-engine.js";
import { EMPTY_FIELD, type ConsolidatedCollaborator, type RdoConsolidatedHeader, type RdoConsolidatedModel } from "../../../domain/rdo/rdo-consolidation.js";
import { AppError, ErrorCode } from "../../../shared/errors/app-error.js";

function formatDateBr(dateStr?: string | null): string {
  if (!dateStr) return '-';
  if (dateStr.includes('/')) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
}

function formatHours(val?: number | null): string {
  if (val === undefined || val === null || isNaN(val)) return '0,00';
  return Number(val).toFixed(2).replace('.', ',');
}

// ---------------------------------------------------------------------------
// RDO INDIVIDUAL BUILDER
// Strict implementation of INSPECT_IA_V11_Modelo_RDO_Individual.pdf
// ---------------------------------------------------------------------------
export class RdoBuilder implements DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions {
    const data = snapshot.data || {};
    const rdo = data.rdo || {};

    const clientName = (data.clientName || rdo.clientName || 'CLIENTE').toUpperCase();
    const workOrderCode = (data.workOrderCode || rdo.workOrderCode || rdo.workOrderId || '-').toUpperCase();
    const subWorkOrderCode = (data.subWorkOrderCode || rdo.subWorkOrderCode || rdo.subWorkOrderId || '-').toUpperCase();
    const serviceFrontName = (data.serviceFrontName || rdo.serviceFrontName || rdo.serviceFrontId || '-').toUpperCase();
    const professionalName = (data.professionalName || rdo.professionalName || rdo.professionalId || 'COLABORADOR').toUpperCase();
    const functionName = (rdo.functionName || 'INSPETOR DE EQUIPAMENTOS').toUpperCase();
    const shift = (rdo.shift || 'ADMINISTRATIVO').toUpperCase();
    const om = (rdo.om || '-').toUpperCase();
    const dateFormatted = formatDateBr(rdo.date);
    const startTime = rdo.startTime || '07:30';
    const endTime = rdo.endTime || '17:18';
    const normalHours = rdo.normalHours ?? 8.8;
    const overtimeHours = rdo.overtimeHours ?? 0;
    const description = rdo.description || 'Execução de atividades de inspeção e acompanhamento técnico.';
    const emissionDate = data.emissionDate || new Date().toLocaleDateString('pt-BR');
    const docNumber = data.documentNumber || snapshot.documentNumber || `RDO-${(rdo.date || '').replace(/-/g, '')}-${snapshot.id?.substring(0, 6) || '001'}`.toUpperCase();

    const content: Content[] = [
      // 1. HEADER BOX (TABLE)
      {
        table: {
          widths: ['25%', '55%', '20%'],
          body: [
            [
              {
                text: 'INSPECT IA',
                style: 'headerBrand',
                alignment: 'center',
                margin: [0, 4, 0, 2]
              },
              {
                text: 'RELATÓRIO ANALÍTICO DE APONTAMENTO DE HORAS TRABALHADAS',
                style: 'headerMainTitle',
                alignment: 'center',
                margin: [0, 4, 0, 2]
              },
              {
                stack: [
                  { text: 'Nº RDO', style: 'headerNumberLabel' },
                  { text: docNumber, style: 'headerNumberVal' }
                ],
                alignment: 'center',
                margin: [0, 2, 0, 2]
              }
            ],
            [
              {
                text: 'Integridade & Inspeção',
                style: 'headerSubBrand',
                alignment: 'center',
                margin: [0, 1, 0, 2]
              },
              {
                text: 'APONTAMENTO INDIVIDUAL',
                style: 'headerSubTitleCenter',
                alignment: 'center',
                margin: [0, 1, 0, 2]
              },
              {
                text: 'Gerado automaticamente',
                style: 'headerGenAuto',
                alignment: 'center',
                margin: [0, 1, 0, 2]
              }
            ]
          ]
        },
        layout: {
          hLineWidth: (i, node) => 1,
          vLineWidth: (i, node) => 1,
          hLineColor: () => '#475569',
          vLineColor: () => '#475569'
        },
        margin: [0, 0, 0, 4]
      },

      // 2. METADATA GRID (2 rows x 3 cols)
      {
        table: {
          widths: ['33.33%', '33.33%', '33.34%'],
          body: [
            [
              {
                stack: [
                  { text: 'FRENTE DE SERVIÇO', style: 'metaLabel' },
                  { text: serviceFrontName, style: 'metaValue' }
                ],
                margin: [4, 3, 4, 3]
              },
              {
                stack: [
                  { text: 'SUB-OS', style: 'metaLabel' },
                  { text: subWorkOrderCode, style: 'metaValue' }
                ],
                margin: [4, 3, 4, 3]
              },
              {
                stack: [
                  { text: 'PERÍODO', style: 'metaLabel' },
                  { text: dateFormatted, style: 'metaValue' }
                ],
                margin: [4, 3, 4, 3]
              }
            ],
            [
              {
                stack: [
                  { text: 'CLIENTE', style: 'metaLabel' },
                  { text: clientName, style: 'metaValue' }
                ],
                margin: [4, 3, 4, 3]
              },
              {
                stack: [
                  { text: 'OS', style: 'metaLabel' },
                  { text: workOrderCode, style: 'metaValue' }
                ],
                margin: [4, 3, 4, 3]
              },
              {
                stack: [
                  { text: 'DATA EMISSÃO', style: 'metaLabel' },
                  { text: emissionDate, style: 'metaValue' }
                ],
                margin: [4, 3, 4, 3]
              }
            ]
          ]
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#94a3b8',
          vLineColor: () => '#94a3b8'
        },
        margin: [0, 0, 0, 6]
      },

      // 3. COLLABORATOR HEADER BANNER
      {
        table: {
          widths: ['100%'],
          body: [
            [
              {
                text: [
                  { text: 'COLABORADOR: ', style: 'collabLabel' },
                  { text: professionalName, style: 'collabValue' }
                ],
                fillColor: '#f1f5f9',
                margin: [6, 4, 6, 4]
              }
            ]
          ]
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#64748b',
          vLineColor: () => '#64748b'
        },
        margin: [0, 0, 0, 4]
      },

      // 4. POINT TABLE
      {
        table: {
          widths: ['12%', '10%', '10%', '11%', '13%', '22%', '11%', '11%'],
          body: [
            [
              { text: 'DATA', style: 'tblHeader', alignment: 'center' },
              { text: 'HORA INICIAL', style: 'tblHeader', alignment: 'center' },
              { text: 'HORA FINAL', style: 'tblHeader', alignment: 'center' },
              { text: 'HORA EXTRA', style: 'tblHeader', alignment: 'center' },
              { text: 'OM', style: 'tblHeader', alignment: 'center' },
              { text: 'FUNÇÃO', style: 'tblHeader', alignment: 'center' },
              { text: 'TURNO', style: 'tblHeader', alignment: 'center' },
              { text: 'OS', style: 'tblHeader', alignment: 'center' }
            ],
            [
              { text: dateFormatted, style: 'tblCell', alignment: 'center' },
              { text: startTime, style: 'tblCell', alignment: 'center' },
              { text: endTime, style: 'tblCell', alignment: 'center' },
              { text: formatHours(overtimeHours), style: 'tblCell', alignment: 'center' },
              { text: om, style: 'tblCell', alignment: 'center' },
              { text: functionName, style: 'tblCell', alignment: 'center' },
              { text: shift, style: 'tblCell', alignment: 'center' },
              { text: workOrderCode, style: 'tblCell', alignment: 'center' }
            ]
          ]
        },
        layout: {
          fillColor: (rowIndex) => (rowIndex === 0 ? '#e2e8f0' : null),
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#cbd5e1',
          vLineColor: () => '#cbd5e1'
        },
        margin: [0, 0, 0, 4]
      },

      // 5. SERVICE DESCRIPTION
      {
        table: {
          widths: ['100%'],
          body: [
            [
              {
                text: 'DESCRIÇÃO DO SERVIÇO',
                style: 'sectionBanner',
                fillColor: '#e2e8f0',
                margin: [4, 2, 4, 2]
              }
            ],
            [
              {
                text: description,
                style: 'descBody',
                margin: [6, 6, 6, 8]
              }
            ]
          ]
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#cbd5e1',
          vLineColor: () => '#cbd5e1'
        },
        margin: [0, 0, 0, 4]
      },

      // 6. TOTAIS DO COLABORADOR
      {
        table: {
          widths: ['56%', '22%', '22%'],
          body: [
            [
              { text: 'TOTAIS DO COLABORADOR', style: 'totaisCollabTitle', margin: [6, 4, 6, 4] },
              {
                text: [{ text: 'DIÁRIAS: ', style: 'metaLabel' }, { text: '1', style: 'totaisVal' }],
                alignment: 'center',
                margin: [4, 4, 4, 4]
              },
              {
                text: [{ text: 'HORAS EXTRA: ', style: 'metaLabel' }, { text: formatHours(overtimeHours), style: 'totaisVal' }],
                alignment: 'center',
                margin: [4, 4, 4, 4]
              }
            ]
          ]
        },
        layout: {
          fillColor: () => '#f8fafc',
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#94a3b8',
          vLineColor: () => '#94a3b8'
        },
        margin: [0, 0, 0, 6]
      },

      // 7. RESUMO DO CLIENTE (DARK BANNER)
      {
        table: {
          widths: ['56%', '22%', '22%'],
          body: [
            [
              { text: 'RESUMO DO CLIENTE', style: 'clientSummaryHeader', margin: [6, 4, 6, 4] },
              { text: 'DIÁRIAS', style: 'clientSummaryHeader', alignment: 'center', margin: [4, 4, 4, 4] },
              { text: 'HORAS EXTRA', style: 'clientSummaryHeader', alignment: 'center', margin: [4, 4, 4, 4] }
            ],
            [
              { text: 'TOTAIS DO CLIENTE', style: 'clientSummaryValLabel', margin: [6, 4, 6, 4] },
              { text: '1', style: 'clientSummaryVal', alignment: 'center', margin: [4, 4, 4, 4] },
              { text: formatHours(overtimeHours), style: 'clientSummaryVal', alignment: 'center', margin: [4, 4, 4, 4] }
            ]
          ]
        },
        layout: {
          fillColor: (rowIndex) => (rowIndex === 0 ? '#1e293b' : '#f8fafc'),
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#334155',
          vLineColor: () => '#334155'
        },
        margin: [0, 0, 0, 10]
      },

      // 8. SIGNATURES BLOCK
      {
        table: {
          widths: ['50%', '50%'],
          body: [
            [
              {
                stack: [
                  { text: 'Assinatura e Carimbo do Cliente:', style: 'sigLabel', margin: [0, 0, 0, 18] },
                  { text: '____________________________________________________', alignment: 'center', color: '#64748b' }
                ],
                margin: [8, 6, 8, 6]
              },
              {
                stack: [
                  { text: 'Assinatura e Carimbo da Contratada:', style: 'sigLabel', margin: [0, 0, 0, 18] },
                  { text: '____________________________________________________', alignment: 'center', color: '#64748b' }
                ],
                margin: [8, 6, 8, 6]
              }
            ]
          ]
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#cbd5e1',
          vLineColor: () => '#cbd5e1'
        },
        margin: [0, 0, 0, 0]
      }
    ];

    return {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [25, 20, 25, 25],
      content,
      footer: (currentPage, pageCount) => ({
        margin: [25, 0, 25, 0],
        columns: [
          { text: 'INSPECT IA | RDO Individual', style: 'footerText' },
          { text: `Página ${currentPage} de ${pageCount}`, style: 'footerText', alignment: 'right' }
        ]
      }),
      styles: {
        headerBrand: { fontSize: 13, bold: true, color: '#0f172a' },
        headerMainTitle: { fontSize: 10, bold: true, color: '#0f172a' },
        headerNumberLabel: { fontSize: 6.5, bold: true, color: '#64748b' },
        headerNumberVal: { fontSize: 8.5, bold: true, color: '#0f172a' },
        headerSubBrand: { fontSize: 7, color: '#475569' },
        headerSubTitleCenter: { fontSize: 7.5, bold: true, color: '#0f172a' },
        headerGenAuto: { fontSize: 6.5, color: '#64748b' },
        metaLabel: { fontSize: 6.5, bold: true, color: '#64748b' },
        metaValue: { fontSize: 8, bold: true, color: '#0f172a' },
        collabLabel: { fontSize: 8, bold: true, color: '#334155' },
        collabValue: { fontSize: 8.5, bold: true, color: '#0f172a' },
        tblHeader: { fontSize: 7, bold: true, color: '#1e293b' },
        tblCell: { fontSize: 7.5, color: '#0f172a' },
        sectionBanner: { fontSize: 7, bold: true, color: '#1e293b' },
        descBody: { fontSize: 7.5, color: '#1e293b', lineHeight: 1.25 },
        totaisCollabTitle: { fontSize: 7.5, bold: true, color: '#1e293b' },
        totaisVal: { fontSize: 8, bold: true, color: '#0f172a' },
        clientSummaryHeader: { fontSize: 7.5, bold: true, color: '#ffffff' },
        clientSummaryValLabel: { fontSize: 7.5, bold: true, color: '#1e293b' },
        clientSummaryVal: { fontSize: 8, bold: true, color: '#0f172a' },
        sigLabel: { fontSize: 7, bold: true, color: '#334155' },
        footerText: { fontSize: 6.5, color: '#94a3b8' }
      }
    };
  }
}

// ---------------------------------------------------------------------------
// RDO CONSOLIDATED BUILDER
// Strict implementation of INSPECT_IA_V11_Modelo_RDO_Consolidado.pdf
// ---------------------------------------------------------------------------

/**
 * RDO CONSOLIDADO — um documento por Projeto + Frente de Serviço + período.
 *
 * Consome o RdoConsolidatedModel, o mesmo objeto que o preview usa, e imprime
 * cada apontamento como um registro próprio, com a descrição exatamente como
 * foi salva. Nada é concatenado, resumido ou preenchido por invenção: campo
 * ausente sai como "—".
 *
 * Paginação: cada colaborador é UMA tabela com duas linhas de cabeçalho
 * repetidas (headerRows: 2), então o nome do colaborador e os títulos das
 * colunas reaparecem sozinhos quando o bloco continua na página seguinte. Não
 * há nó unbreakable envolvendo o bloco inteiro — era isso que impedia a
 * paginação de um colaborador com muitos registros —, e não há quebra forçada,
 * de modo que nenhuma página em branco é criada.
 */
export class RdoConsolidatedBuilder implements DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions {
    const model: RdoConsolidatedModel | null = snapshot.data?.model ?? null;
    if (!model) {
      throw new AppError(ErrorCode.Validation, "RDO_CONSOLIDATED snapshot has no model", {
        safeMessage: "O RDO Consolidado não pôde ser montado: o documento veio sem os apontamentos."
      });
    }

    const header = model.header;
    const upper = (value: string) => (value || EMPTY_FIELD).toUpperCase();
    const content: Content[] = [];

    for (const collaborator of model.collaborators) {
      content.push(this.buildCollaboratorTable(collaborator));
      content.push(this.buildCollaboratorTotals(collaborator));
    }

    content.push(this.buildSummary(model));
    content.push(this.buildSignatures());

    return {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [25, 104, 25, 34],
      // Cabeçalho documental repetido em todas as páginas (compacto).
      header: () => this.buildDocumentHeader(header, upper),
      footer: (currentPage, pageCount) => ({
        margin: [25, 8, 25, 0],
        columns: [
          { text: `INSPECT IA | RDO Consolidado | ${upper(header.serviceFrontName)}`, style: 'footerText' },
          { text: `Página ${currentPage} de ${pageCount}`, style: 'footerText', alignment: 'right' }
        ]
      }),
      content,
      styles: {
        headerBrand: { fontSize: 12, bold: true, color: '#0f172a' },
        headerMainTitle: { fontSize: 10, bold: true, color: '#0f172a' },
        headerNumberLabel: { fontSize: 6.5, bold: true, color: '#64748b' },
        headerNumberVal: { fontSize: 8, bold: true, color: '#0f172a' },
        metaLabel: { fontSize: 6.5, bold: true, color: '#64748b' },
        metaValue: { fontSize: 8, bold: true, color: '#0f172a' },
        collabLabel: { fontSize: 8, bold: true, color: '#334155' },
        collabValue: { fontSize: 9, bold: true, color: '#0f172a' },
        tblHeader: { fontSize: 7, bold: true, color: '#1e293b' },
        tblCell: { fontSize: 7.5, color: '#0f172a' },
        recordLabel: { fontSize: 6.5, bold: true, color: '#64748b' },
        descBody: { fontSize: 7.5, color: '#1e293b', lineHeight: 1.25 },
        totaisCollabTitle: { fontSize: 7.5, bold: true, color: '#1e293b' },
        totaisVal: { fontSize: 8, bold: true, color: '#0f172a' },
        summaryHeader: { fontSize: 7.5, bold: true, color: '#ffffff' },
        summaryValLabel: { fontSize: 7.5, bold: true, color: '#1e293b' },
        summaryVal: { fontSize: 8, bold: true, color: '#0f172a' },
        sigLabel: { fontSize: 7, bold: true, color: '#334155' },
        footerText: { fontSize: 6.5, color: '#94a3b8' }
      }
    };
  }

  /** Cabeçalho repetido: título, nº, Projeto, Frente, período, cliente, CNPJ, sigla. */
  private buildDocumentHeader(header: RdoConsolidatedHeader, upper: (v: string) => string): Content {
    const periodStart = formatDateBr(header.periodStart);
    const periodEnd = formatDateBr(header.periodEnd);
    const periodText = periodStart === periodEnd ? periodStart : `${periodStart} a ${periodEnd}`;

    const metaCell = (label: string, value: string): TableCell => ({
      stack: [
        { text: label, style: 'metaLabel' },
        { text: value, style: 'metaValue' }
      ],
      margin: [4, 3, 4, 3]
    });

    return {
      margin: [25, 14, 25, 0],
      stack: [
        {
          table: {
            widths: ['22%', '58%', '20%'],
            body: [[
              { text: 'INSPECT IA', style: 'headerBrand', alignment: 'center', margin: [0, 5, 0, 5] },
              {
                text: 'RELATÓRIO ANALÍTICO DE APONTAMENTO DE HORAS TRABALHADAS',
                style: 'headerMainTitle',
                alignment: 'center',
                margin: [0, 5, 0, 5]
              },
              {
                stack: [
                  { text: 'Nº DO RDO', style: 'headerNumberLabel' },
                  { text: header.documentNumber, style: 'headerNumberVal' },
                  { text: `REV. ${header.documentRevision}`, style: 'headerNumberLabel' }
                ],
                alignment: 'center',
                margin: [0, 3, 0, 3]
              }
            ]]
          },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => '#475569',
            vLineColor: () => '#475569'
          },
          margin: [0, 0, 0, 3]
        },
        {
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: [
              [
                metaCell('PROJETO', upper(header.projectName)),
                metaCell('FRENTE DE SERVIÇO', upper(header.serviceFrontName)),
                metaCell('PERÍODO', periodText),
                metaCell('DATA DE EMISSÃO', formatDateBr(header.emissionDate))
              ],
              [
                metaCell('CLIENTE', upper(header.clientName)),
                metaCell('CNPJ', header.clientDocument || EMPTY_FIELD),
                metaCell('SIGLA', upper(header.clientAcronym)),
                metaCell('REVISÃO', header.documentRevision)
              ]
            ]
          },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => '#94a3b8',
            vLineColor: () => '#94a3b8'
          }
        }
      ]
    };
  }

  /**
   * Um colaborador = uma tabela. As duas primeiras linhas (faixa com o nome e
   * títulos das colunas) são headerRows, então o pdfmake as repete quando o
   * bloco atravessa a página — é a rastreabilidade da continuação.
   *
   * Cada apontamento ocupa duas linhas: a dos campos e a da descrição. Uma
   * linha de data aparece sempre que a data muda.
   */
  private buildCollaboratorTable(collaborator: ConsolidatedCollaborator): Content {
    const COLS = 8;
    const filler = () => Array.from({ length: COLS - 1 }, () => ({ text: '' } as TableCell));

    const body: TableCell[][] = [
      [
        {
          text: [
            { text: 'COLABORADOR: ', style: 'collabLabel' },
            { text: (collaborator.name || EMPTY_FIELD).toUpperCase(), style: 'collabValue' },
            { text: `   ·   FUNÇÃO: ${(collaborator.functionName || EMPTY_FIELD).toUpperCase()}`, style: 'collabLabel' }
          ],
          colSpan: COLS,
          fillColor: '#f1f5f9',
          margin: [6, 4, 6, 4]
        },
        ...filler()
      ],
      [
        { text: 'DATA', style: 'tblHeader', alignment: 'center' },
        { text: 'HORA INICIAL', style: 'tblHeader', alignment: 'center' },
        { text: 'HORA FINAL', style: 'tblHeader', alignment: 'center' },
        { text: 'HORA EXTRA', style: 'tblHeader', alignment: 'center' },
        { text: 'OM', style: 'tblHeader', alignment: 'center' },
        { text: 'FUNÇÃO', style: 'tblHeader', alignment: 'center' },
        { text: 'TURNO', style: 'tblHeader', alignment: 'center' },
        { text: 'OS', style: 'tblHeader', alignment: 'center' }
      ]
    ];

    for (const day of collaborator.days) {
      // Separador visual da data — abre o conjunto de registros do dia.
      body.push([
        {
          text: `DATA: ${formatDateBr(day.date)}`,
          colSpan: COLS,
          style: 'recordLabel',
          fillColor: '#f8fafc',
          margin: [6, 3, 6, 3]
        },
        ...filler()
      ]);

      day.records.forEach((record, index) => {
        body.push([
          { text: formatDateBr(record.date), style: 'tblCell', alignment: 'center' },
          { text: record.startTime || '—', style: 'tblCell', alignment: 'center' },
          { text: record.endTime || '—', style: 'tblCell', alignment: 'center' },
          { text: formatHours(record.overtimeHours), style: 'tblCell', alignment: 'center' },
          { text: record.om || '—', style: 'tblCell', alignment: 'center' },
          { text: (record.functionName || EMPTY_FIELD).toUpperCase(), style: 'tblCell', alignment: 'center' },
          { text: (record.shift || EMPTY_FIELD).toUpperCase(), style: 'tblCell', alignment: 'center' },
          { text: (record.workOrderCode || EMPTY_FIELD).toUpperCase(), style: 'tblCell', alignment: 'center' }
        ]);

        const descriptionStack: Content[] = [
          {
            text: [
              { text: `REGISTRO ${String(index + 1).padStart(2, '0')} · DESCRIÇÃO DO SERVIÇO: `, style: 'recordLabel' },
              // Texto exatamente como salvo. Vazio vira "—", nunca uma frase inventada.
              { text: record.description || EMPTY_FIELD, style: 'descBody' }
            ]
          }
        ];

        if (record.inspectorComment) {
          descriptionStack.push({
            text: [
              { text: 'COMENTÁRIO DO FISCAL: ', style: 'recordLabel' },
              { text: record.inspectorComment, style: 'descBody' }
            ],
            margin: [0, 3, 0, 0]
          });
        }

        body.push([
          { stack: descriptionStack, colSpan: COLS, margin: [6, 4, 6, 4] },
          ...filler()
        ]);
      });
    }

    return {
      table: {
        // Repete nome do colaborador e títulos das colunas em cada página.
        headerRows: 2,
        // Uma linha de registro nunca é partida ao meio entre páginas.
        dontBreakRows: true,
        widths: ['11%', '10%', '10%', '11%', '13%', '22%', '11%', '12%'],
        body
      },
      layout: {
        fillColor: (rowIndex: number) => (rowIndex === 1 ? '#e2e8f0' : null),
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#cbd5e1',
        vLineColor: () => '#cbd5e1'
      },
      margin: [0, 0, 0, 2]
    };
  }

  /** Totais só depois de TODOS os registros do colaborador no período. */
  private buildCollaboratorTotals(collaborator: ConsolidatedCollaborator): Content {
    return {
      unbreakable: true,
      table: {
        widths: ['46%', '18%', '18%', '18%'],
        body: [[
          { text: 'TOTAIS DO COLABORADOR', style: 'totaisCollabTitle', margin: [6, 3, 6, 3] },
          {
            text: [{ text: 'DIÁRIAS: ', style: 'metaLabel' }, { text: String(collaborator.totalDays), style: 'totaisVal' }],
            alignment: 'center', margin: [4, 3, 4, 3]
          },
          {
            text: [{ text: 'HORAS NORMAIS: ', style: 'metaLabel' }, { text: formatHours(collaborator.totalNormalHours), style: 'totaisVal' }],
            alignment: 'center', margin: [4, 3, 4, 3]
          },
          {
            text: [{ text: 'HORAS EXTRA: ', style: 'metaLabel' }, { text: formatHours(collaborator.totalOvertimeHours), style: 'totaisVal' }],
            alignment: 'center', margin: [4, 3, 4, 3]
          }
        ]]
      },
      layout: {
        fillColor: () => '#f8fafc',
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#94a3b8',
        vLineColor: () => '#94a3b8'
      },
      margin: [0, 0, 0, 8]
    };
  }

  /** O documento é por Frente, então o fechamento é RESUMO DO RDO — não do cliente. */
  private buildSummary(model: RdoConsolidatedModel): Content {
    return {
      unbreakable: true,
      table: {
        widths: ['28%', '18%', '18%', '18%', '18%'],
        body: [
          [
            { text: 'RESUMO DO RDO', style: 'summaryHeader', margin: [6, 4, 6, 4] },
            { text: 'COLABORADORES', style: 'summaryHeader', alignment: 'center', margin: [4, 4, 4, 4] },
            { text: 'REGISTROS', style: 'summaryHeader', alignment: 'center', margin: [4, 4, 4, 4] },
            { text: 'DIÁRIAS', style: 'summaryHeader', alignment: 'center', margin: [4, 4, 4, 4] },
            { text: 'HORAS EXTRA', style: 'summaryHeader', alignment: 'center', margin: [4, 4, 4, 4] }
          ],
          [
            { text: (model.header.serviceFrontName || EMPTY_FIELD).toUpperCase(), style: 'summaryValLabel', margin: [6, 4, 6, 4] },
            { text: String(model.totalCollaborators), style: 'summaryVal', alignment: 'center', margin: [4, 4, 4, 4] },
            { text: String(model.totalRecords), style: 'summaryVal', alignment: 'center', margin: [4, 4, 4, 4] },
            { text: String(model.totalDays), style: 'summaryVal', alignment: 'center', margin: [4, 4, 4, 4] },
            { text: formatHours(model.totalOvertimeHours), style: 'summaryVal', alignment: 'center', margin: [4, 4, 4, 4] }
          ]
        ]
      },
      layout: {
        fillColor: (rowIndex: number) => (rowIndex === 0 ? '#1e293b' : '#f8fafc'),
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#334155',
        vLineColor: () => '#334155'
      },
      margin: [0, 6, 0, 8]
    };
  }

  private buildSignatures(): Content {
    const block = (label: string): TableCell => ({
      stack: [
        { text: label, style: 'sigLabel', margin: [0, 0, 0, 20] },
        { text: '____________________________________________________', alignment: 'center', color: '#64748b' }
      ],
      margin: [8, 6, 8, 6]
    });

    return {
      unbreakable: true,
      table: {
        widths: ['50%', '50%'],
        body: [[
          block('Assinatura e Carimbo do Cliente:'),
          block('Assinatura / Responsável INSPECT IA:')
        ]]
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#cbd5e1',
        vLineColor: () => '#cbd5e1'
      }
    };
  }
}

// Register both builders on the global documentEngine singleton
documentEngine.registerBuilder("RDO", new RdoBuilder());
documentEngine.registerBuilder("RDO_INDIVIDUAL", new RdoBuilder());
documentEngine.registerBuilder("RDO_CONSOLIDATED", new RdoConsolidatedBuilder());
